import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, Badge, Button } from '@/shared/ui';
import { SectionHeader } from '../components/SectionHeader';
import { StatusPills } from '../components/StatusPills';
import { QuestionInput } from './QuestionInput';
import type { StrategyQuestion } from '../api/strategyApi';
import type { SectionKey } from '../constants/strategySections';
import { STRATEGY_SECTIONS } from '../constants/strategySections';
import styles from './PracticeProfileStep.module.css';

const isEmptyAnswer = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

type SectionDataItem = {
  sec: { readonly key: SectionKey; readonly title: string; readonly dbSections: readonly string[] };
  questions: StrategyQuestion[];
};

function subSectionFillStatus(
  qs: StrategyQuestion[],
  responses: Record<string, unknown>,
): 'filled' | 'partially-filled' | null {
  if (qs.length === 0) return null;
  const answered = qs.filter((q) => {
    const v = responses[q.id];
    return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;
  if (answered === qs.length) return 'filled';
  if (answered > 0) return 'partially-filled';
  return null;
}

interface Props {
  sectionData: readonly SectionDataItem[];
  openSubSection: string | null;
  setOpenSubSection: Dispatch<SetStateAction<string | null>>;
  confirmedSections: Set<string>;
  section1Done: boolean;
  section2Done: boolean;
  setSection1Expanded: Dispatch<SetStateAction<boolean>>;
  subSectionHeaderRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  pendingScrollToKey: MutableRefObject<string | null>;
  hasVapiCall: boolean;
  responses: Record<string, unknown>;
  setResponse: (questionId: string, value: unknown) => void;
  confirmSection: (sectionKey: SectionKey, sectionQuestions: StrategyQuestion[]) => void;
}

export function StrategicInputsSection({
  sectionData,
  openSubSection,
  setOpenSubSection,
  confirmedSections,
  section1Done,
  section2Done,
  setSection1Expanded,
  subSectionHeaderRefs,
  pendingScrollToKey,
  hasVapiCall,
  responses,
  setResponse,
  confirmSection,
}: Props) {
  return (
    <>
      <SectionHeader n={2} done={section2Done} title="Your strategic inputs" estimate="~4 min" />

      <div className={styles.sectionList}>
        {sectionData.map(({ sec, questions: sectionQuestions }) => {
          if (sectionQuestions.length === 0) return null;
          const isSubOpen = openSubSection === sec.key;
          const fillStatus = subSectionFillStatus(sectionQuestions, responses);
          const isConfirmed = confirmedSections.has(sec.key);
          const source = hasVapiCall ? 'phone' : null;
          const missingRequired = sectionQuestions.filter(
            (q) => q.required && isEmptyAnswer(responses[q.id]),
          ).length;

          return (
            <Card key={sec.key} padding="none" className={styles.accordionCard}>
              <div
                ref={(el) => {
                  subSectionHeaderRefs.current[sec.key] = el;
                }}
                onClick={() => {
                  const isOpening = openSubSection !== sec.key;
                  if (isOpening) {
                    pendingScrollToKey.current = sec.key;
                    if (section1Done) setSection1Expanded(false);
                  }
                  setOpenSubSection((p) => (p === sec.key ? null : sec.key));
                }}
                className={styles.accordionHeader}
              >
                {isConfirmed && (
                  <span className={styles.accentIcon}>
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                )}
                <span className={styles.accordionHeaderTitle}>{sec.title}</span>
                <StatusPills fill={fillStatus} source={source} confirmed={isConfirmed} />
                <span className={styles.accordionChevron}>
                  {isSubOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                </span>
              </div>

              {isSubOpen && (
                <div className={styles.accordionBody}>
                  {sectionQuestions.map((q) => (
                    <div key={q.id}>
                      <div
                        className={`${styles.questionLabel} ${q.description ? styles.questionLabelWithDesc : ''}`}
                      >
                        {q.label}
                        {q.required && <span className={styles.requiredAsterisk}>*</span>}
                      </div>
                      {q.description && <div className={styles.questionDesc}>{q.description}</div>}
                      <QuestionInput
                        question={q}
                        value={responses[q.id]}
                        onChange={(v) => setResponse(q.id, v)}
                      />
                    </div>
                  ))}

                  <div className={styles.confirmFooter} onClick={(e) => e.stopPropagation()}>
                    {isConfirmed ? (
                      <Badge variant="primary">
                        <span className={styles.badgeContent}>
                          <Check size={11} aria-hidden /> Confirmed
                        </span>
                      </Badge>
                    ) : (
                      <>
                        {missingRequired > 0 && (
                          <span className={styles.confirmHint}>
                            Answer the {missingRequired} required question
                            {missingRequired === 1 ? '' : 's'} (*) to confirm
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={missingRequired > 0}
                          onClick={() => confirmSection(sec.key, sectionQuestions)}
                        >
                          Confirm section
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}

// Re-export so parent can use the const array as the prop type
export { STRATEGY_SECTIONS };
